import { createSelector } from 'reselect';
import moment from 'moment-timezone';
import utils from 'state/shipments/utils';
import {
  constants as dateAndTimeConstants,
  methods as dateAndTimeMethods,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';
import { appointmentRequired, constants as orderConstants } from 'utils/order';
import { selectors as errorsSelectors } from 'state/errors';
import { selectors as warningsSelectors } from 'state/warnings';

const getState = (state) => state.shipments;
const getExternalProps = (
  _,
  shipmentKey,
  stopType,
  timeParam,
  loads,
  orderType
) => ({
  shipmentKey,
  stopType,
  timeParam,
  loads,
  orderType,
});

const getShipment = createSelector(
  getState,
  getExternalProps,
  (shipments, { shipmentKey }) => shipments[shipmentKey] || {}
);

const getShipmentTimes = createSelector(getShipment, (shipment) =>
  dateAndTimeMethods.getTimesFromShipment(shipment)
);

const getShipmentExtras = createSelector(
  getState,
  getExternalProps,
  (shipments, { shipmentKey }) =>
    (shipments[shipmentKey] || { extras: [] }).extras
);

const getShipmentTimeRaw = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { timeParam }) => {
    return shipment[`${timeParam}`];
  }
);

const getShipmentTimesRaw = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) => ({
    [dateAndTimeConstants.TIME_FIELD_PARAMS.from]:
      shipment[`${stopType}_${dateAndTimeConstants.TIME_FIELD_PARAMS.from}`],
    [dateAndTimeConstants.TIME_FIELD_PARAMS.to]:
      shipment[`${stopType}_${dateAndTimeConstants.TIME_FIELD_PARAMS.to}`],
  })
);

const getShipmentTimesErrors = createSelector(
  errorsSelectors.getErrors,
  getExternalProps,
  (errors) => errors
);

const getShipmentTimesWarnings = createSelector(
  warningsSelectors.getWarnings,
  getExternalProps,
  (warnings) => warnings
);

const getShipmentDate = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) => shipment[`${stopType}_date_from`]
);

const getShipmentTimezone = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) =>
    shipment[`${stopType}_timezone`] || moment.tz.guess()
);

const getMinAndMaxForDates = createSelector(
  getShipmentTimes,
  (state) => state.order.validations,
  getExternalProps,
  (
    shipmentTimes,
    validations,
    { stopType, timeParam, orderType = orderConstants.DIRECT_ORDER }
  ) =>
    dateAndTimeValidators.getRulesWithRestrictions({
      times: shipmentTimes,
      isForSuggestions: { dates: true, times: false },
      validations,
    })[`${stopType}_${timeParam}`]
);

const getInternalReference = createSelector(
  getShipment,
  getExternalProps,
  (shipment) => shipment.internal_reference
);

const getShipmentAddress = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) => shipment[utils.getAddressKey(stopType)] || {}
);

const getShipmentRawAddress = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) => shipment[utils.getAddressRawKey(stopType)]
);

const getCommentsForTransportist = createSelector(
  getShipment,
  getExternalProps,
  (shipment, { stopType }) =>
    shipment[`${stopType || 'pickup'}_comment_from_shipper_to_driver`]
);

const getLoadsInShipment = createSelector(
  getState,
  getExternalProps,
  (shipments, { shipmentKey }) => {
    return shipments[shipmentKey]?.loads;
  }
);

const getHasAppointment = createSelector(
  getState,
  (state) => state.order.type,
  getExternalProps,
  (shipments, orderType, { shipmentKey }) => {
    const shipment = shipments[shipmentKey];
    return appointmentRequired({
      orderType,
      stopType: 'dropoff',
      poi_info: shipment?.address_to?.poi_info,
      appointment_on_demand: shipment?.dropoff_appointment_on_demand,
    });
  }
);

export default {
  getState,
  getShipment,
  getShipmentExtras,
  getShipmentTimes,
  getShipmentTimeRaw,
  getShipmentTimesRaw,

  getShipmentTimesErrors,
  getShipmentTimesWarnings,

  getShipmentDate,
  getShipmentTimezone,

  getMinAndMaxForDates,

  getShipmentAddress,
  getShipmentRawAddress,

  getInternalReference,
  getCommentsForTransportist,

  getLoadsInShipment,
  getHasAppointment,
};
